import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {IEmploymentInfo} from '../../../../../models/employment-info';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {StatusPair} from '../../../../../models/status-pair';
import {NatureOfBusiness} from '../../../../../models/nature-of-business';
import {Country, StateInfo} from '../../../../../models/country';
import {IAddressInfo} from '../../../../../models/address-info';
import {CountryService} from '../../../../../data-repositories/country-service';
import {EmploymentStatusData} from '../../../../../data-repositories/employment-status-data';
import {NatureOfBusinessData} from '../../../../../data-repositories/nature-of-business-data';
import {nonWhitespace, validateOptionalRequired} from '../../../../../services/validators';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {FocusInvalidService} from '../../../../../services/focus-invalid.service';
import {ActivatedRoute} from '@angular/router';
import {Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {formLog} from '../../../../../services/debug-logger';

@Component({
  standalone: false,
  selector: 'app-employment-citizenship',
  templateUrl: './employment-citizenship.component.html',
  styleUrls: ['./employment-citizenship.component.scss']
})
export class EmploymentCitizenshipComponent implements OnInit, OnDestroy {
  employmentForm: FormGroup;
  employmentStatusList: Array<StatusPair>;
  natureOfBusinessList: Array<NatureOfBusiness>;
  countryList: Array<Country>;
  @Input() addressModel: IAddressInfo;
  @Output() employmentModelChange = new EventEmitter<IEmploymentInfo>();
  @ViewChild('employmentFormElement') employmentFormElement: ElementRef;
  private subscription$: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private validationService: ValidationInfoService,
    private focusInvalidService: FocusInvalidService,
    private route: ActivatedRoute,
    countryService: CountryService,
    employmentStatusData: EmploymentStatusData,
    natureOfBusinessData: NatureOfBusinessData,
  ) {
    this.buildForm();

    countryService.getCountryList().pipe(takeUntil(this.destroy$)).subscribe(cl => {
      this.countryList = cl;
      this.onAddressCountryChanged(false);
    });

    this.employmentStatusList = employmentStatusData.getEmploymentStatusList();
    this.natureOfBusinessList = natureOfBusinessData.getNatureOfBusinessList();
  }

  private _employmentModel: IEmploymentInfo;

  get employmentModel(): IEmploymentInfo {
    return this._employmentModel;
  }

  @Input()
  set employmentModel(value: IEmploymentInfo) {
    this._employmentModel = value;
    this.patchFormFromModel();
    this.onAddressCountryChanged(false);
  }

  ngOnInit() {
    this.patchFormFromModel();

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params.validate) {
        this.validationService.markAllFieldsAsTouched(this.employmentForm);
        this.focusInvalidService.selectInvalidInput(this.employmentFormElement);
      }
    });

    this.subscription$ = this.validationService.onUserUpdate.subscribe(() => {
      this.validationService.markAllFieldsAsTouched(this.employmentForm);

      this.focusInvalidService.selectInvalidInput(this.employmentFormElement);
      this.validationService.validate(this.employmentForm.valid && this.isCorrectEmployerAddress(), 'EmploymentCitizenshipComponent');
    });

    this.subscribeAllInputsOnUpdate();
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  updateUser(): void {
    this.employmentModelChange.emit(this.employmentModel);
  }

  isRequiredField(fieldName: string): boolean {
    return this.employmentForm.controls[fieldName].invalid
      && (this.employmentForm.controls[fieldName].dirty || this.employmentForm.controls[fieldName].touched)
      && (this.employmentForm.controls[fieldName].errors.required || this.employmentForm.controls[fieldName].errors.pattern);
  }

  isEmployed(): boolean {
    return this.employmentModel &&
      (this.employmentModel.employmentStatus === 'EMPLOYED' ||
        this.employmentModel.employmentStatus === 'SELFEMPLOYED');
  }

  isRequiredCountryDetails() {
    return this.addressModel.countryName &&
      this.employmentModel.employerCountry &&
      this.addressModel.countryName !== this.employmentModel.employerCountry;
  }

  filterCountryList(countryList: Array<Country>, filter: string, includeUSA: boolean = true) {
    if (!countryList) {
      return new Array<Country>();
    }

    if (filter && filter.trim().length > 0) {
      return countryList.filter(x => x.countryName !== 'United States' || includeUSA)
        .filter(x => x.countryTranslation.toLowerCase().startsWith(filter.toLowerCase()));
    }

    return countryList.filter(x => x.countryName !== 'United States' || includeUSA);
  }

  getCountryCode(citizenship: string): string {
    if (citizenship && this.countryList && this.countryList.some(x => x.countryName === citizenship)) {
      return this.countryList.find(x => x.countryName === citizenship).countryCode;
    }

    return '';
  }

  getTranslate(country: string) {
    if (!country || !this.countryList || !this.countryList.some(x => x.countryName === country)) {
      return '';
    }

    return this.countryList.find(x => x.countryName === country).countryTranslation;
  }

  getOccupationList() {
    if (!this.employmentModel.natureOfBussiness) {
      return this.natureOfBusinessList[0].businessList;
    } else {
      return this.natureOfBusinessList.find(nof => nof.natureName === this.employmentModel.natureOfBussiness).businessList;
    }
  }

  isCorrectEmployerAddress() {
    if (this.employmentModel.employmentStatus === 'EMPLOYED') {
      return !this.equalString(this.addressModel.countryName, this.employmentModel.employerCountry) ||
        !this.equalString(this.addressModel.address1, this.employmentModel.address1) ||
        !this.equalString(this.addressModel.address2, this.employmentModel.address2) ||
        !this.equalString(this.addressModel.state, this.employmentModel.state) ||
        !this.equalString(this.addressModel.city, this.employmentModel.city) ||
        !this.equalString(this.addressModel.postalCode, this.employmentModel.postalCode);
    }

    return true;
  }

  getStatesByCountry(countryName: string): Array<StateInfo> {
    if (countryName && this.countryList) {
      const country = this.countryList.find(x => x.countryName === countryName);
      return country.states;
    }

    return new Array<StateInfo>();
  }

  getStateName(countryName: string, stateCode: string) {
    if (countryName && stateCode) {
      const countryStates = this.getStatesByCountry(countryName);
      if (countryStates.some(x => x.code === stateCode)) {
        return countryStates.find(x => x.code === stateCode).name;
      }
    }

    return '';
  }

  onAddressCountryChanged(reset: boolean = true) {
    if (reset) {
      this.employmentModel.state = null;
      this.updateUser();
    }

    const hasStates = !!this.employmentModel && this.getStatesByCountry(this.employmentModel.employerCountry).length > 0;
    const state = this.employmentForm.controls.employerState;

    if (hasStates) {
      state.enable();
    } else {
      state.disable();
    }
    state.setValidators(hasStates ? validateOptionalRequired(() => this.isEmployed()) : null);
    if (reset) {
      state.setValue(null);
    }

    if (hasStates && this.employmentModel.employerCountry === 'Israel') {
      const israelStateCode = this.getStatesByCountry(this.employmentModel.employerCountry)[0].code;
      this.employmentModel.state = israelStateCode;
      state.setValue(israelStateCode);
      this.updateUser();
    }
    state.updateValueAndValidity();
  }

  private buildForm() {
    this.employmentForm = this.formBuilder.group({
      employerCountry: new FormControl('', [validateOptionalRequired(() => this.isEmployed())]),
      employerCountryDetails: new FormControl('', [validateOptionalRequired(() => this.isEmployed() && this.isRequiredCountryDetails()), nonWhitespace()]),
      employerState: new FormControl('', [validateOptionalRequired(() => this.isEmployed())]),
      employerAddress: new FormControl('', [validateOptionalRequired(() => this.isEmployed()), nonWhitespace()]),
      employerAddress2: new FormControl('', [validateOptionalRequired(() => this.isEmployed()), nonWhitespace()]),
      employerCity: new FormControl('', [validateOptionalRequired(() => this.isEmployed()), nonWhitespace()]),
      employerPostalCode: new FormControl('', [validateOptionalRequired(() => this.isEmployed()), nonWhitespace()]),
    });
  }

  private equalString(a: string, b: string) {
    if (!a || !b) {
      return false;
    }

    return a.trim().replace(/\s+/g, '').toLowerCase() === b.trim().replace(/\s+/g, '').toLowerCase();
  }

  private subscribeAllInputsOnUpdate() {
    this.employmentForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.syncModelFromForm();
      this.updateUser();
    });
  }

  private patchFormFromModel(): void {
    if (!this._employmentModel || !this.employmentForm) {
      return;
    }

    this.employmentForm.patchValue({
      employerCountry: this._employmentModel.employerCountry ?? '',
      employerCity: this._employmentModel.city ?? '',
      employerAddress: this._employmentModel.address1 ?? '',
      employerAddress2: this._employmentModel.address2 ?? '',
      employerPostalCode: this._employmentModel.postalCode ?? '',
      employerState: this._employmentModel.state ?? '',
      employerCountryDetails: this._employmentModel.employerCountryDetails ?? '',
    }, {emitEvent: false});
    formLog.state('EmploymentCitizenship', this._employmentModel, this.employmentForm);
  }

  private syncModelFromForm(): void {
    if (!this._employmentModel) {
      return;
    }

    const value = this.employmentForm.getRawValue();
    this._employmentModel.employerCountry = value.employerCountry;
    this._employmentModel.city = value.employerCity;
    this._employmentModel.address1 = value.employerAddress;
    this._employmentModel.address2 = value.employerAddress2;
    this._employmentModel.postalCode = value.employerPostalCode;
    this._employmentModel.state = value.employerState;
    this._employmentModel.employerCountryDetails = value.employerCountryDetails;
    formLog.state('EmploymentCitizenship', this._employmentModel, this.employmentForm);
  }
}
